import React from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraText,
  Counter,
  Dot,
  EquipUITheme,
} from '@equip.health/ui';

import { ChatUser } from '~/lib/types/chat';
import { getChannelAvatarName } from '~/lib/util/chat/chat.utils';
import {
  fileExtensionToIconMapper,
  getDocumentTypeFromFileName,
} from '~/lib/util/chat/chatAttachments.util';

import ChatAvatar from './ChatAvatar';
import ChatGroupChannelAvatar from './ChatGroupChannelAvatar';
import MembersPopoverList from './MembersPopoverList';
import MessageDotIndicator from './NewMessageIndicator';

const idPrefix = 'channel';
const idPostfix = 'tile';

const { paragraphBold, label } = EquipUITheme.typography;

interface ChannelTileProps {
  channelMembers: ChatUser[]; // TODO: need to use Member[] from uikit
  channelTitle: string;
  hasUnreadMessages: boolean;
  hideMembersPopover?: boolean;
  id: string;
  isExternalChannel: boolean;
  isChannelInactiveRotated?: boolean;
  /** When true, drops the tile's own background/radius/padding so it can sit as a flat, divider-separated row (e.g. the overview rail). */
  isFlat?: boolean;
  isLastFile: boolean;
  isSelected: boolean;
  lastMessage: string;
  lastMessageRelativeDate: string;
  onClick: () => void;
  renderMarkAsRead?: React.ReactElement;
  unreadCount?: number;
}

const ChannelTile = React.forwardRef<HTMLInputElement, ChannelTileProps>(
  (
    {
      channelMembers,
      channelTitle,
      hasUnreadMessages,
      id,
      isExternalChannel,
      isChannelInactiveRotated,
      isFlat = false,
      isLastFile,
      isSelected,
      lastMessage,
      lastMessageRelativeDate,
      onClick,
      hideMembersPopover = false,
      renderMarkAsRead,
      unreadCount,
    }: ChannelTileProps,
    ref?: React.ForwardedRef<HTMLInputElement>,
  ) => {
    const fileType = getDocumentTypeFromFileName(lastMessage);
    const FileIcon = fileExtensionToIconMapper[fileType];

    const getBackgroundColor = () => {
      if (isFlat) return 'transparent';
      return isSelected ? 'primary.10' : 'neutral.background.primary';
    };

    const containerProps = {
      alignItems: 'flex-start',
      backgroundColor: getBackgroundColor(),
      borderColor: isSelected ? 'primary.50' : 'neutral.line',
      borderRadius: isFlat ? 0 : '16px',
      cursor: 'pointer',
      gridGap: channelMembers?.length > 0 ? '12px' : '0',
      padding: isFlat ? 0 : '16px',
      width: '100%',
    };

    // In the rail these size to the space the avatar leaves, keeping the date inside the tile.
    const textColumnProps = isFlat
      ? { flex: 1, minWidth: 0 }
      : { width: '100%' };
    const messageBoxProps = isFlat
      ? { flex: 1, minWidth: 0 }
      : { width: '190px' };

    // A one-line band keeps the dot and members icon level with a two-line title's first line.
    const firstLineBandProps = {
      alignItems: 'center',
      flexShrink: 0,
      gridGap: '6px',
      height: paragraphBold.lineHeight,
    };

    // The title row's 6px gap applies to empty bands too, so each renders only when it has content.
    const hasLeadingIndicators = hasUnreadMessages || isChannelInactiveRotated;
    const hasTrailingIndicators =
      !!unreadCount ||
      (!hideMembersPopover && (channelMembers ?? []).length > 0);

    return (
      <ChakraFlex
        direction="column"
        id={`${idPrefix}-container__${id}__${idPostfix}`}
        ref={ref}
        {...containerProps}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <ChakraFlex gridGap="16px" width="100%">
          <ChakraBox
            alignItems="center"
            id={`${idPrefix}-avatar-container__${id}__${idPostfix}`}
          >
            {isExternalChannel || (channelMembers ?? []).length === 0 ? (
              <ChatAvatar
                id={`${idPrefix}-avatar__${id}__${idPostfix}`}
                name={getChannelAvatarName(channelTitle)}
              />
            ) : (
              <ChatGroupChannelAvatar
                user1Nickname={channelMembers[1]?.nickname}
                user2Nickname={channelMembers[0]?.nickname}
              />
            )}
          </ChakraBox>
          <ChakraFlex
            direction="column"
            justifyContent="center"
            {...textColumnProps}
          >
            <ChakraFlex>
              <ChakraFlex
                alignItems={isFlat ? 'flex-start' : 'center'}
                gridGap="6px"
                id={`${idPrefix}-channel-title-wrapper__${id}__${idPostfix}`}
                justifyContent="start"
                width="100%"
              >
                {hasLeadingIndicators && (
                  <ChakraFlex {...firstLineBandProps}>
                    {hasUnreadMessages && (
                      <MessageDotIndicator id={id} isShowDot />
                    )}
                    {isChannelInactiveRotated && (
                      <MessageDotIndicator dotColor="red" id={id} isShowDot />
                    )}
                  </ChakraFlex>
                )}
                <ChakraText
                  {...paragraphBold}
                  id={`${idPrefix}-channel-title__${id}__${idPostfix}`}
                >
                  {channelTitle}
                </ChakraText>
                {hasTrailingIndicators && (
                  <ChakraFlex {...firstLineBandProps}>
                    {!!unreadCount && <Counter id={id} number={unreadCount} />}
                    {!hideMembersPopover &&
                      (channelMembers ?? []).length > 0 && (
                        <MembersPopoverList id={id} members={channelMembers} />
                      )}
                  </ChakraFlex>
                )}
              </ChakraFlex>
              {renderMarkAsRead && (
                <ChakraFlex
                  alignContent="right"
                  id={`${idPrefix}-channel-mark-as-unread-wrapper__${id}__${idPostfix}`}
                  justifyContent="right"
                  width="fit-content"
                >
                  {renderMarkAsRead}
                </ChakraFlex>
              )}
            </ChakraFlex>
            {(isLastFile || lastMessage) && lastMessageRelativeDate && (
              <ChakraFlex
                id={`${idPrefix}-channel-message-wrapper__${id}__${idPostfix}`}
                justifyContent="space-between"
              >
                <ChakraBox {...messageBoxProps}>
                  <ChakraText
                    {...label}
                    id={`${idPrefix}-channel-last-message__${id}__${idPostfix}`}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {isLastFile ? (
                      <ChakraFlex alignItems="center">
                        <ChakraBox>
                          <FileIcon height={16} width={16} />
                        </ChakraBox>
                        <ChakraText paddingLeft="6px">{lastMessage}</ChakraText>
                      </ChakraFlex>
                    ) : (
                      lastMessage
                    )}
                  </ChakraText>
                </ChakraBox>
                <ChakraFlex
                  id={`${idPrefix}-channel-last-message-date-wrapper__${id}__${idPostfix}`}
                  textAlign="right"
                  width="fit-content"
                >
                  <ChakraBox paddingTop="10px" paddingX="5px">
                    <Dot />
                  </ChakraBox>
                  <ChakraText
                    textTransform="capitalize"
                    {...label}
                    id={`${idPrefix}-channel-last-message-date__${id}__${idPostfix}`}
                  >
                    {lastMessageRelativeDate}
                  </ChakraText>
                </ChakraFlex>
              </ChakraFlex>
            )}
          </ChakraFlex>
        </ChakraFlex>
      </ChakraFlex>
    );
  },
);

ChannelTile.displayName = 'ChannelTile';

export default ChannelTile;
